import { ethers } from "ethers";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RPC_URLS = {
  Ethereum: [
    "https://ethereum.publicnode.com",
    "https://1rpc.io/eth",
    "https://eth.llamarpc.com",
  ],
  Arbitrum: [
    "https://arbitrum-one.publicnode.com",
    "https://1rpc.io/arb",
    "https://arb1.arbitrum.io/rpc",
  ],
  Optimism: [
    "https://optimism.publicnode.com",
    "https://1rpc.io/op",
    "https://mainnet.optimism.io",
  ],
  Base: [
    "https://base.publicnode.com",
    "https://1rpc.io/base",
    "https://mainnet.base.org",
  ],
  Polygon: [
    "https://polygon-bor.publicnode.com",
    "https://1rpc.io/matic",
    "https://polygon-rpc.com",
  ],
};

// Try multiple RPCs until one works
async function getProvider(chain) {
  const urls = RPC_URLS[chain];
  if (!urls || urls.length === 0) throw new Error(`No RPC configured for ${chain}`);

  for (const url of urls) {
    try {
      const provider = new ethers.JsonRpcProvider(url, undefined, {
        staticNetwork: true,
        batchMaxCount: 1,
      });
      // Quick test — if this fails, try next RPC
      await provider.getBlockNumber();
      return provider;
    } catch {
      continue;
    }
  }
  throw new Error(`All RPCs failed for ${chain}. Try again in a moment.`);
}

const NFT_MANAGER_ADDRESSES = {
  Ethereum: "0xC36442b4a4522E871399CD717aBDD847Ab11FE88",
  Arbitrum: "0xC36442b4a4522E871399CD717aBDD847Ab11FE88",
  Optimism: "0xC36442b4a4522E871399CD717aBDD847Ab11FE88",
  Polygon: "0xC36442b4a4522E871399CD717aBDD847Ab11FE88",
  Base: "0x03a520b32C04BF3bEEf7BEb72E919cf822Ed34f1",
};

const FACTORY_ADDRESSES = {
  Ethereum: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
  Arbitrum: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
  Optimism: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
  Polygon: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
  Base: "0x33128a8fC17869897dcE68Ed026d694621f6FDfD",
};

const NFT_MANAGER_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function tokenOfOwnerByIndex(address, uint256) view returns (uint256)",
  "function positions(uint256) view returns (uint96, address, address, address, uint24, int24, int24, uint128, uint256, uint256, uint128, uint128)",
];

const POOL_ABI = [
  "function slot0() view returns (uint160, int24, uint16, uint16, uint16, uint8, bool)",
];

const FACTORY_ABI = [
  "function getPool(address, address, uint24) view returns (address)",
];

const ERC20_ABI = [
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
];

const LLAMA_PRICE_URL = "https://coins.llama.fi/prices/current";

const CHAIN_SLUGS = {
  Ethereum: "ethereum",
  Arbitrum: "arbitrum",
  Optimism: "optimism",
  Base: "base",
  Polygon: "polygon",
};

// ---------------------------------------------------------------------------
// Token metadata cache (symbol + decimals) — shared across calls
// ---------------------------------------------------------------------------

const _tokenCache = new Map(); // key: `${chain}:${address}` → { symbol, decimals }

async function getTokenMeta(provider, tokenAddress, chain) {
  const key = `${chain}:${tokenAddress.toLowerCase()}`;
  if (_tokenCache.has(key)) return _tokenCache.get(key);

  try {
    const erc20 = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    const [symbol, decimals] = await Promise.all([
      erc20.symbol(),
      erc20.decimals(),
    ]);
    const meta = { symbol, decimals: Number(decimals) };
    _tokenCache.set(key, meta);
    return meta;
  } catch (err) {
    console.warn(`Failed to fetch token meta for ${tokenAddress} on ${chain}:`, err.message);
    const fallback = { symbol: "???", decimals: 18 };
    _tokenCache.set(key, fallback);
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// Uniswap V3 math — compute token amounts from position parameters
// ---------------------------------------------------------------------------

export function computeTokenAmounts(
  liquidity,
  tickLower,
  tickUpper,
  currentTick,
  sqrtPriceX96,
  decimals0,
  decimals1
) {
  // Convert sqrtPriceX96 (BigInt or BigNumber) to a regular Number.
  // sqrtPriceX96 can be extremely large, so we divide by 2^96 first.
  const Q96 = 2 ** 96;
  const sqrtPrice = Number(sqrtPriceX96) / Q96;

  const sqrtPriceLower = Math.sqrt(1.0001 ** tickLower);
  const sqrtPriceUpper = Math.sqrt(1.0001 ** tickUpper);

  const L = Number(liquidity);
  let amount0Raw = 0;
  let amount1Raw = 0;

  if (currentTick < tickLower) {
    // Position is entirely in token0
    amount0Raw = L * (1 / sqrtPriceLower - 1 / sqrtPriceUpper);
    amount1Raw = 0;
  } else if (currentTick >= tickUpper) {
    // Position is entirely in token1
    amount0Raw = 0;
    amount1Raw = L * (sqrtPriceUpper - sqrtPriceLower);
  } else {
    // In range
    amount0Raw = L * (1 / sqrtPrice - 1 / sqrtPriceUpper);
    amount1Raw = L * (sqrtPrice - sqrtPriceLower);
  }

  const amount0 = amount0Raw / 10 ** decimals0;
  const amount1 = amount1Raw / 10 ** decimals1;

  return { amount0, amount1 };
}

// ---------------------------------------------------------------------------
// Fetch token prices from DeFi Llama
// ---------------------------------------------------------------------------

export async function fetchTokenPrices(tokenAddresses, chain) {
  const slug = CHAIN_SLUGS[chain];
  if (!slug) {
    console.warn(`Unknown chain slug for "${chain}"`);
    return new Map();
  }

  const keys = tokenAddresses.map((a) => `${slug}:${a.toLowerCase()}`);
  const url = `${LLAMA_PRICE_URL}/${keys.join(",")}`;

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`DeFi Llama prices responded ${res.status}`);
    const json = await res.json();

    const prices = new Map();
    for (const [fullKey, data] of Object.entries(json.coins || {})) {
      const addr = fullKey.split(":")[1];
      prices.set(addr.toLowerCase(), {
        price: data.price,
        symbol: data.symbol,
        decimals: data.decimals,
      });
    }
    return prices;
  } catch (err) {
    console.warn("Failed to fetch token prices:", err.message);
    return new Map();
  }
}

// ---------------------------------------------------------------------------
// Main: fetch all Uniswap V3 positions for a wallet on a given chain
// ---------------------------------------------------------------------------

export async function fetchWalletPositions(walletAddress, chain, onProgress) {
  const nftManagerAddr = NFT_MANAGER_ADDRESSES[chain];
  const factoryAddr = FACTORY_ADDRESSES[chain];
  if (!nftManagerAddr || !factoryAddr) {
    throw new Error(`Chain "${chain}" is not supported.`);
  }

  if (onProgress) onProgress(`Connecting to ${chain}...`);
  const provider = await getProvider(chain);

  const nftManager = new ethers.Contract(nftManagerAddr, NFT_MANAGER_ABI, provider);
  const factory = new ethers.Contract(factoryAddr, FACTORY_ABI, provider);

  // ---- Step 1: get number of positions owned ----
  if (onProgress) onProgress('Reading wallet positions...');
  let balance;
  try {
    balance = Number(await nftManager.balanceOf(walletAddress));
  } catch (err) {
    throw new Error(`Failed to read wallet on ${chain}. RPC may be overloaded — try again.`);
  }

  if (balance === 0) return [];

  // Cap at 50 to avoid excessive RPC calls
  const count = Math.min(balance, 50);

  // ---- Step 2: get token IDs ----
  let tokenIds;
  try {
    tokenIds = await Promise.all(
      Array.from({ length: count }, (_, i) =>
        nftManager.tokenOfOwnerByIndex(walletAddress, i)
      )
    );
  } catch (err) {
    throw new Error(`Failed to fetch position IDs on ${chain}. RPC may be overloaded — try again.`);
  }

  // ---- Step 3: get position data for each token ID ----
  const positionResults = await Promise.allSettled(
    tokenIds.map((id) => nftManager.positions(id))
  );

  // Pair tokenId with position data, filter out failures and closed positions
  const activePositions = [];
  for (let i = 0; i < tokenIds.length; i++) {
    const result = positionResults[i];
    if (result.status !== "fulfilled") {
      console.warn(`Failed to fetch position ${tokenIds[i]} on ${chain}:`, result.reason?.message);
      continue;
    }
    const pos = result.value;
    // positions() returns: nonce, operator, token0, token1, fee, tickLower, tickUpper,
    //                      liquidity, feeGrowthInside0LastX128, feeGrowthInside1LastX128,
    //                      tokensOwed0, tokensOwed1
    const liquidity = pos[7];
    if (liquidity === 0n) continue; // closed position

    activePositions.push({
      tokenId: tokenIds[i],
      token0Addr: pos[2],
      token1Addr: pos[3],
      feeTier: Number(pos[4]),
      tickLower: Number(pos[5]),
      tickUpper: Number(pos[6]),
      liquidity,
      tokensOwed0: pos[10],
      tokensOwed1: pos[11],
    });
  }

  if (activePositions.length === 0) return [];

  // ---- Step 4: collect unique token addresses and fetch metadata ----
  const uniqueTokens = new Set();
  for (const p of activePositions) {
    uniqueTokens.add(p.token0Addr);
    uniqueTokens.add(p.token1Addr);
  }

  // Fetch token metadata (symbol, decimals) in parallel
  await Promise.all(
    [...uniqueTokens].map((addr) => getTokenMeta(provider, addr, chain))
  );

  // ---- Step 5: fetch pool slot0 for each position (pool address + current tick) ----
  if (onProgress) onProgress('Fetching pool data...');
  const enrichResults = await Promise.allSettled(
    activePositions.map(async (p) => {
      const poolAddress = await factory.getPool(p.token0Addr, p.token1Addr, p.feeTier);
      if (poolAddress === ethers.ZeroAddress) {
        throw new Error(`Pool not found for ${p.token0Addr}/${p.token1Addr} fee ${p.feeTier}`);
      }
      const poolContract = new ethers.Contract(poolAddress, POOL_ABI, provider);
      const slot0 = await poolContract.slot0();
      return {
        sqrtPriceX96: slot0[0],
        currentTick: Number(slot0[1]),
      };
    })
  );

  // ---- Step 6: fetch prices from DeFi Llama ----
  if (onProgress) onProgress('Loading prices...');
  const allAddresses = [...uniqueTokens];
  const prices = await fetchTokenPrices(allAddresses, chain);

  // ---- Step 7: assemble final position objects ----
  const positions = [];
  for (let i = 0; i < activePositions.length; i++) {
    const p = activePositions[i];
    const enrichResult = enrichResults[i];

    if (enrichResult.status !== "fulfilled") {
      console.warn(
        `Failed to enrich position ${p.tokenId} on ${chain}:`,
        enrichResult.reason?.message
      );
      continue;
    }

    const { sqrtPriceX96, currentTick } = enrichResult.value;

    const meta0 = _tokenCache.get(`${chain}:${p.token0Addr.toLowerCase()}`);
    const meta1 = _tokenCache.get(`${chain}:${p.token1Addr.toLowerCase()}`);
    const decimals0 = meta0?.decimals ?? 18;
    const decimals1 = meta1?.decimals ?? 18;

    // Compute current token amounts
    const { amount0, amount1 } = computeTokenAmounts(
      p.liquidity,
      p.tickLower,
      p.tickUpper,
      currentTick,
      sqrtPriceX96,
      decimals0,
      decimals1
    );

    // Uncollected fees (convert from raw to human-readable)
    const tokensOwed0 = Number(p.tokensOwed0) / 10 ** decimals0;
    const tokensOwed1 = Number(p.tokensOwed1) / 10 ** decimals1;

    // Prices
    const priceInfo0 = prices.get(p.token0Addr.toLowerCase());
    const priceInfo1 = prices.get(p.token1Addr.toLowerCase());
    const price0 = priceInfo0?.price ?? 0;
    const price1 = priceInfo1?.price ?? 0;

    const amount0Usd = amount0 * price0;
    const amount1Usd = amount1 * price1;
    const totalValueUsd = amount0Usd + amount1Usd;
    const feesUsd = tokensOwed0 * price0 + tokensOwed1 * price1;

    const inRange = currentTick >= p.tickLower && currentTick < p.tickUpper;

    positions.push({
      tokenId: Number(p.tokenId),
      token0: {
        address: p.token0Addr,
        symbol: meta0?.symbol ?? "???",
        decimals: decimals0,
      },
      token1: {
        address: p.token1Addr,
        symbol: meta1?.symbol ?? "???",
        decimals: decimals1,
      },
      feeTier: p.feeTier,
      tickLower: p.tickLower,
      tickUpper: p.tickUpper,
      currentTick,
      liquidity: p.liquidity.toString(),
      amount0,
      amount1,
      amount0Usd,
      amount1Usd,
      totalValueUsd,
      tokensOwed0,
      tokensOwed1,
      feesUsd,
      inRange,
      chain,
    });
  }

  return positions;
}
